import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { matchCustomerSites } from '../../utils/geofence';
import { formatDate } from '../../utils/date';
import {
  getTimeEntries, getActiveTimeEntry, getTodayTimeSummary,
  clockIn as apiClockIn, clockOut as apiClockOut, discardTimeEntry,
  forgotClockOut as apiForgotClockOut,
  getWorkOrders, getSites,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Clock, Play, Square, MapPin, X, AlertTriangle, Briefcase,
  Building2, Truck, Home,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Two-lane time clock:
 *
 *   Typical day:
 *     1. Clock in at Lutrom  (pick up van)
 *     2. Drive to customer   (Lutrom clock still running)
 *     3. Clock in at customer (both lanes now active)
 *     4. Work the job
 *     5. Clock out at customer
 *     6. Drive back
 *     7. Clock out at Lutrom
 *
 *  The backend enforces one entry per lane — an engineer can't double-clock
 *  at Lutrom or at two customers at once. The UI mirrors that with two
 *  independent control cards.
 */
const TimeClock = () => {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  // Two-lane shape: {lutrom: entry|null, customer: entry|null}
  const [active, setActive] = useState({ lutrom: null, customer: null });
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  // Picker state for the "Clock in at customer" dialog.
  const [pickerOpen, setPickerOpen] = useState(false);
  // HQ-geofence reason dialog state for the "Clock in at Lutrom" lane —
  // opens automatically when the backend returns a 422 saying the
  // engineer is outside the HQ site radius.
  const [hqReasonOpen, setHqReasonOpen] = useState(false);
  const [hqReasonInfo, setHqReasonInfo] = useState(null); // {distance, radius, siteName}
  const [hqReason, setHqReason] = useState('');
  // Forgot-to-clock-out self-correction. Auto-detected when an open
  // entry's clockIn is older than the threshold below — engineer types
  // the time they finished and the entry closes server-side with a
  // `forgotToClockOut=True` flag for admin visibility.
  const FORGOT_THRESHOLD_HOURS = 18;
  const [forgotTarget, setForgotTarget] = useState(null); // open entry doc
  const [forgotTime, setForgotTime] = useState('');
  const [forgotNote, setForgotNote] = useState('');
  const [forgotSaving, setForgotSaving] = useState(false);
  // Entry ids already submitted (or snoozed) this session — the detector
  // must never reopen for them, even before the refreshed data arrives.
  const forgotHandledRef = useRef(new Set());
  const forgotSubmittingRef = useRef(false);
  const tickRef = useRef(null);
  const [today, setToday] = useState({
    clockedHours: 0, jobHours: 0, unaccountedHours: 0,
    customerHours: 0, atLutromHours: 0,
    customerWoHours: 0, lutromWoHours: 0,
    customerUnaccounted: 0, lutromUnaccounted: 0,
  });

  const refreshSummary = () => getTodayTimeSummary().then(setToday).catch(() => {});
  const refreshEntries = () => getTimeEntries().then(setEntries).catch(() => {});
  const refreshActive = () => getActiveTimeEntry().then((a) => {
    // Backend now returns `{lutrom, customer}` — but be liberal in what we
    // accept in case an older response sneaks through.
    if (a && (a.lutrom !== undefined || a.customer !== undefined)) {
      setActive({ lutrom: a.lutrom || null, customer: a.customer || null });
    } else if (a && a.id) {
      const kind = a.kind || (a.siteId ? 'customer' : 'lutrom');
      setActive({ lutrom: kind === 'lutrom' ? a : null, customer: kind === 'customer' ? a : null });
    } else {
      setActive({ lutrom: null, customer: null });
    }
  }).catch(() => {});

  useEffect(() => {
    refreshEntries();
    refreshSummary();
    refreshActive();
    const id = setInterval(refreshSummary, 60_000);
    return () => clearInterval(id);
  }, [user.id]);

  // Live clock — runs while EITHER lane is active.
  useEffect(() => {
    const anyActive = !!(active.lutrom || active.customer);
    if (!anyActive) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    setNow(new Date());
    tickRef.current = setInterval(() => setNow(new Date()), 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [active.lutrom, active.customer]);

  // Detect a stale open clock-in (engineer forgot to clock out yesterday).
  // Picks the OLDEST open lane and surfaces a quick-close dialog. Only
  // opens once per active-state change so the engineer isn't nagged on
  // every refresh.
  useEffect(() => {
    if (forgotTarget) return;
    const cutoff = Date.now() - FORGOT_THRESHOLD_HOURS * 60 * 60 * 1000;
    const stale = [active.lutrom, active.customer]
      .filter(Boolean)
      .filter((e) => !forgotHandledRef.current.has(e.id))
      .filter((e) => new Date(e.clockIn).getTime() < cutoff)
      .sort((a, b) => new Date(a.clockIn) - new Date(b.clockIn));
    if (!stale.length) return;
    const target = stale[0];
    setForgotTarget(target);
    // Default the typed time to "5pm on the day they clocked in" — the
    // most common forgot-to-clock-out scenario is finishing a normal
    // shift and never tapping stop.
    const startedDate = new Date(target.clockIn);
    setForgotTime(`${String(17).padStart(2, '0')}:00`);
    setForgotNote('');
    // Stash the started date in the dialog target so we can rebuild a
    // full ISO from the typed HH:MM at submit time.
    target.__startedDate = startedDate;
  }, [active.lutrom, active.customer, forgotTarget]);

  // Submit the engineer's claimed clock-out → backend closes the entry,
  // flags forgotToClockOut, pushes notification to admin role.
  const closeForgotDialog = (entryId) => {
    if (entryId) forgotHandledRef.current.add(entryId);
    setForgotTarget(null);
    setForgotTime('');
    setForgotNote('');
  };

  const submitForgotClockOut = async () => {
    if (!forgotTarget || forgotSubmittingRef.current) return;
    if (!forgotTime || !/^\d{2}:\d{2}$/.test(forgotTime)) {
      toast.error('Pick a finish time');
      return;
    }
    forgotSubmittingRef.current = true;
    setForgotSaving(true);
    const entryId = forgotTarget.id;
    try {
      const startedDate = forgotTarget.__startedDate || new Date(forgotTarget.clockIn);
      const yyyy = startedDate.getUTCFullYear();
      const mm = String(startedDate.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(startedDate.getUTCDate()).padStart(2, '0');
      const isoOut = `${yyyy}-${mm}-${dd}T${forgotTime}:00+00:00`;
      await apiForgotClockOut(entryId, {
        clockOut: isoOut,
        breakMins: 0,
        notes: forgotNote.trim(),
      });
      toast.success('Thanks — admin has been notified to review.');
      closeForgotDialog(entryId);
      await Promise.all([refreshActive(), refreshEntries(), refreshSummary()]);
    } catch (e) {
      if (e?.response?.status === 409) {
        // Already closed (e.g. submitted from another tab) — nothing left to fix.
        toast.info('This clock-in was already closed.');
        closeForgotDialog(entryId);
        await Promise.all([refreshActive(), refreshEntries(), refreshSummary()]);
      } else {
        toast.error(e?.response?.data?.detail || 'Could not save — please try again');
      }
    } finally {
      forgotSubmittingRef.current = false;
      setForgotSaving(false);
    }
  };

  const elapsed = (entry) => entry
    ? Math.max(0, Math.floor((now.getTime() - new Date(entry.clockIn).getTime()) / 1000))
    : 0;
  const fmtClock = (s) =>
    `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const captureCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude, lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  });

  // ---- Lutrom lane handlers ----
  const clockInLutrom = async (overrideReason = '') => {
    // Defensive: accept either a string reason or an event-like object
    // (e.g. if called directly from an `onClick` handler).
    const reason = typeof overrideReason === 'string' ? overrideReason : '';
    setBusy(true);
    try {
      const coords = await captureCoords();
      const entry = await apiClockIn({
        kind: 'lutrom',
        ...(coords || {}),
        outsideReason: reason,
      });
      setActive((s) => ({ ...s, lutrom: entry }));
      toast.success(
        reason
          ? `Clocked in at Lutrom (${hqReasonInfo?.distance || '?'}m off-site)`
          : 'Clocked in at Lutrom',
      );
      setHqReasonOpen(false);
      setHqReason('');
      setHqReasonInfo(null);
      refreshSummary();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Geofence soft-warn for the Lutrom lane: backend returns a
      // structured 422 with distance + radius. Open the HQ reason dialog
      // so the engineer can explain why they're clocking in off-site
      // (same pattern as the customer lane).
      if (e?.response?.status === 422 && d && typeof d === 'object' && d.outsideGeofence) {
        setHqReasonInfo({
          distance: d.distanceMeters,
          radius: d.radiusMeters,
          siteName: d.siteName || 'Lutrom HQ',
        });
        setHqReasonOpen(true);
        return;
      }
      toast.error(typeof d === 'string' ? d : 'Clock-in failed');
    } finally {
      setBusy(false);
    }
  };

  const clockOutLutrom = async () => {
    // UX guardrail: block Lutrom-out while customer lane is still open —
    // engineer almost certainly meant to clock out of customer first.
    if (active.customer) {
      return toast.error('Clock out of the customer first, then Lutrom.');
    }
    setBusy(true);
    try {
      const coords = await captureCoords();
      await apiClockOut({ kind: 'lutrom', ...(coords || {}), notes: '', breakMins: 0 });
      setActive((s) => ({ ...s, lutrom: null }));
      await refreshEntries();
      refreshSummary();
      toast.success('Clocked out of Lutrom');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clock-out failed');
    } finally {
      setBusy(false);
    }
  };

  // ---- Customer lane handlers ----
  const clockInCustomer = async ({ workOrderId, siteId, customerId, outsideReason, callOut }) => {
    setBusy(true);
    try {
      const coords = await captureCoords();
      const body = {
        kind: 'customer',
        workOrderId: workOrderId || undefined,
        siteId: siteId || undefined,
        customerId: customerId || undefined,
        outsideReason: outsideReason || '',
        callOut: !!callOut,
        ...(coords || {}),
      };
      const entry = await apiClockIn(body);
      setActive((s) => ({ ...s, customer: entry }));
      toast.success(callOut ? 'Clocked in at customer (call-out)' : 'Clocked in at customer');
      setPickerOpen(false);
      refreshSummary();
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Geofence soft-warn: backend returns a structured 422. Re-open the
      // picker with the reason field visible so the engineer can explain.
      if (e?.response?.status === 422 && d?.outsideGeofence) {
        toast.error(`${d.message} (${d.distanceMeters}m away)`, { duration: 8000 });
        return { needsReason: true, distance: d.distanceMeters };
      }
      toast.error(typeof d === 'string' ? d : 'Clock-in failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const clockOutCustomer = async () => {
    setBusy(true);
    try {
      const coords = await captureCoords();
      const res = await apiClockOut({ kind: 'customer', ...(coords || {}), notes: '', breakMins: 0 });
      setActive((s) => ({ ...s, customer: null }));
      await refreshEntries();
      refreshSummary();
      if (res?.billSkipReason) {
        // Hours saved, but auto-billing skipped — engineer should know
        // so they can flag it with the office.
        toast.warning(
          `Clocked out — but auto-billing was skipped: ${res.billSkipReason}. Ask the office to fix.`,
          { duration: 8000 },
        );
      } else if (res?.billedInvoiceId) {
        toast.success(
          `Clocked out — £${Number(res.billedAmount || 0).toFixed(2)} added to draft ${res.billedInvoiceId}`,
        );
      } else {
        toast.success('Clocked out of customer');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clock-out failed');
    } finally {
      setBusy(false);
    }
  };

  const discard = async (entry) => {
    if (!entry) return;
    if (!window.confirm('Discard this clock-in? No hours will be saved.')) return;
    try {
      await discardTimeEntry(entry.id);
      await refreshActive();
      refreshSummary();
      toast.success('Discarded');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="time-clock-page">
      <PageHeader
        title="Time Clock"
        description="Clock in at Lutrom when you start, again at the customer when you arrive, out at the customer when you leave, and out at Lutrom when you drop the van."
      />

      <TodaySummary today={today} anyActive={!!(active.lutrom || active.customer)} />

      {/* Two lane cards, stacked on mobile, side-by-side from md up. */}
      <div className="grid gap-4 md:grid-cols-2">
        <LaneCard
          title="Lutrom"
          subtitle="Start / end of day at HQ"
          icon={Home}
          accent="slate"
          entry={active.lutrom}
          elapsedSec={elapsed(active.lutrom)}
          fmtClock={fmtClock}
          onStart={() => clockInLutrom('')}
          onStop={clockOutLutrom}
          onDiscard={() => discard(active.lutrom)}
          startLabel="Clock in at Lutrom"
          stopLabel="Clock out at Lutrom"
          busy={busy}
          testId="lane-lutrom"
        />
        <LaneCard
          title="Customer"
          subtitle={active.customer ? (active.customer.customerName || active.customer.siteId || 'On site') : 'On-site leg'}
          icon={Building2}
          accent="emerald"
          entry={active.customer}
          elapsedSec={elapsed(active.customer)}
          fmtClock={fmtClock}
          onStart={() => setPickerOpen(true)}
          onStop={clockOutCustomer}
          onDiscard={() => discard(active.customer)}
          startLabel="Clock in at customer"
          stopLabel="Clock out of customer"
          startDisabled={!active.lutrom}
          startDisabledHint="Clock in at Lutrom first"
          busy={busy}
          testId="lane-customer"
        />
      </div>

      <CustomerClockInDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={clockInCustomer}
        userId={user.id}
        getCoords={captureCoords}
      />

      {/* HQ-geofence reason dialog — opens when the Lutrom clock-in is
          rejected because the engineer is outside the HQ radius. Same
          soft-warn pattern as the customer lane. */}
      <HqReasonDialog
        open={hqReasonOpen}
        info={hqReasonInfo}
        reason={hqReason}
        onReasonChange={setHqReason}
        onCancel={() => {
          setHqReasonOpen(false);
          setHqReason('');
          setHqReasonInfo(null);
        }}
        onConfirm={() => {
          if (!hqReason.trim()) return;
          clockInLutrom(hqReason.trim());
        }}
        busy={busy}
      />

      {/* Forgot-to-clock-out self-correction dialog. Auto-opens when an
          open clock-in is older than the threshold so the engineer can
          retroactively close it. Backend flags `forgotToClockOut=True`
          + notifies admin role. */}
      <ForgotClockOutDialog
        target={forgotTarget}
        time={forgotTime}
        note={forgotNote}
        saving={forgotSaving}
        onTimeChange={setForgotTime}
        onNoteChange={setForgotNote}
        onCancel={() => {
          // "Remind me later" — leaves the entry open (they can still tap
          // Stop on the lane card); snoozed until the page is reloaded.
          if (!forgotSaving) closeForgotDialog(forgotTarget?.id);
        }}
        onConfirm={submitForgotClockOut}
      />

      {/* Recent entries — grouped by day. Tap a day to expand and see
          the same per-lane breakdown the top card shows for today. */}
      <RecentDaysList entries={entries} todaySummary={today} />
    </div>
  );
};

/**
 * Groups a flat list of TimeEntries into date-headed rows. Each row is
 * a button — tapping it fetches that day's summary on demand and slides
 * out the same BreakdownCards used above. Lazy-fetched so we don't
 * hammer the backend when the list is long.
 */
const RecentDaysList = ({ entries, todaySummary }) => {
  const [expandedDate, setExpandedDate] = useState('');
  const [summaryCache, setSummaryCache] = useState({});
  const [loadingDate, setLoadingDate] = useState('');

  const todayIso = new Date().toISOString().slice(0, 10);

  // Group entries into { date: [...entries] } preserving newest-first order.
  const byDay = useMemo(() => {
    const map = new Map();
    entries.forEach(e => {
      const d = e.date || (e.clockIn || '').slice(0, 10);
      if (!d) return;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(e);
    });
    return Array.from(map.entries()).slice(0, 14);
  }, [entries]);

  const toggleDay = async (date) => {
    if (expandedDate === date) { setExpandedDate(''); return; }
    setExpandedDate(date);
    if (summaryCache[date] || loadingDate === date) return;
    // Today never needs a fetch — the top card already has the data.
    if (date === todayIso && todaySummary) {
      setSummaryCache(c => ({ ...c, [date]: todaySummary })); return;
    }
    setLoadingDate(date);
    try {
      const s = await getTodayTimeSummary(date);
      setSummaryCache(c => ({ ...c, [date]: s }));
    } catch {
      setSummaryCache(c => ({ ...c, [date]: { __error: true } }));
    } finally {
      setLoadingDate('');
    }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent days</div>
      <div className="space-y-1.5">
        {byDay.map(([date, dayEntries]) => {
          // "On the clock" for a day is the LUTROM (HQ) lane duration —
          // NOT the naive sum of every entry. The customer lane runs
          // concurrently WITHIN the HQ lane (see the daily-flow docstring
          // at the top of this file), so summing both would double-count
          // any overlap and produce impossible totals like "25h 30m in
          // one day". If a day only has customer entries (unusual —
          // engineer forgot to clock in at HQ), fall back to the sum of
          // customer hours so the row isn't misleadingly 0.
          const isLutrom = (e) => (e.kind || (e.siteId ? 'customer' : 'lutrom')) === 'lutrom';
          const lutromHours = dayEntries.filter(isLutrom)
            .reduce((a, e) => a + Number(e.hours || 0), 0);
          const totalHours = lutromHours > 0
            ? lutromHours
            : dayEntries.reduce((a, e) => a + Number(e.hours || 0), 0);
          const laneCount = new Set(dayEntries.map(e => e.kind || (e.siteId ? 'customer' : 'lutrom'))).size;
          const isOpen = expandedDate === date;
          const isToday = date === todayIso;
          const summary = summaryCache[date] || (isToday ? todaySummary : null);
          return (
            <div key={date} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`recent-day-${date}`}>
              <button
                type="button"
                onClick={() => toggleDay(date)}
                className="w-full flex items-center gap-3 text-left p-3 hover:bg-slate-50 transition-colors"
                data-testid={`recent-day-toggle-${date}`}
                aria-expanded={isOpen}
              >
                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${isToday ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  <Clock className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {isToday ? 'Today' : formatDate(date)}
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                      {dayEntries.length} entr{dayEntries.length === 1 ? 'y' : 'ies'}
                      {laneCount > 1 && ' · 2 lanes'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums">
                    {fmtHours(totalHours)} on the clock
                  </div>
                </div>
                <span className={`text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} aria-hidden>›</span>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50 p-3" data-testid={`recent-day-panel-${date}`}>
                  {summary && !summary.__error ? (
                    <DaySummaryPanel summary={summary} entries={dayEntries} />
                  ) : summary?.__error ? (
                    <div className="text-xs text-red-600">Couldn&apos;t load the summary for this day.</div>
                  ) : (
                    <div className="text-xs text-slate-500">Loading…</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {entries.length === 0 && (
          <div className="text-sm text-slate-500 italic">No entries yet — your first clock-in will appear here.</div>
        )}
      </div>
    </div>
  );
};

/**
 * The per-day expanded panel — mirrors the layout of the top-of-page
 * "today" card so the mental model stays identical whether the user is
 * looking at now or a past day.
 */
const DaySummaryPanel = ({ summary, entries }) => {
  const dayTotal = summary.clockedHours || 0;
  const custTotal = summary.customerHours || 0;
  const custWo = summary.customerWoHours || 0;
  const custUnacc = summary.customerUnaccounted ?? Math.max(0, custTotal - custWo);
  const hqTotal = summary.atLutromHours ?? Math.max(0, dayTotal - custTotal);
  const hqWo = summary.lutromWoHours || 0;
  const hqUnacc = summary.lutromUnaccounted ?? Math.max(0, hqTotal - hqWo);
  return (
    <div className="space-y-2">
      <div className="rounded-md border bg-slate-900 border-slate-900 text-white p-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 opacity-90 text-[10px] uppercase tracking-wider">
          <Clock className="h-3 w-3" />
          <span>Total for the day</span>
        </div>
        <div className="font-display text-xl tabular-nums leading-tight">{fmtHours(dayTotal)}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <BreakdownCard
          icon={Building2}
          label="At customer"
          tone="bg-emerald-50 border-emerald-200 text-emerald-900"
          total={custTotal}
          woHours={custWo}
          unaccounted={custUnacc}
          testId="day-breakdown-customer"
        />
        <BreakdownCard
          icon={Home}
          label="At Lutrom (HQ + travel)"
          tone="bg-sky-50 border-sky-200 text-sky-900"
          total={hqTotal}
          woHours={hqWo}
          unaccounted={hqUnacc}
          testId="day-breakdown-lutrom"
        />
      </div>
      {/* Individual clock-ins that make up the day — same tiny row
          format we had before, kept for context. */}
      <div className="pt-1 space-y-1">
        {entries.map(e => {
          const kind = e.kind || (e.siteId ? 'customer' : 'lutrom');
          const Icon = kind === 'lutrom' ? Home : Building2;
          return (
            <div key={e.id} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`recent-entry-${e.id}`}>
              <Icon className={`h-3 w-3 shrink-0 ${kind === 'lutrom' ? 'text-slate-400' : 'text-emerald-600'}`} />
              <span className="tabular-nums">
                {e.clockIn && new Date(e.clockIn).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                {e.clockOut && ` → ${new Date(e.clockOut).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
              </span>
              <span className="ml-auto tabular-nums font-medium">{Number(e.hours || 0).toFixed(2)}h</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** One lane (Lutrom or Customer). Kept visually distinct by the `accent`
 *  prop — slate for Lutrom (neutral, "you're at HQ"), emerald for
 *  customer (green, "you're on the clock for a job"). */
const LaneCard = ({
  title, subtitle, icon: Icon, accent, entry,
  elapsedSec, fmtClock,
  onStart, onStop, onDiscard,
  startLabel, stopLabel,
  startDisabled, startDisabledHint,
  busy, testId,
}) => {
  const active = !!entry;
  const runningTone = accent === 'emerald' ? 'bg-emerald-700 text-white' : 'bg-slate-900 text-white';
  const stopTone = accent === 'emerald' ? 'bg-rose-700 hover:bg-rose-800' : 'bg-rose-700 hover:bg-rose-800';
  const startTone = accent === 'emerald' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-slate-900 hover:bg-slate-800';

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" data-testid={testId}>
      <div className={`px-4 py-3 border-b border-slate-200 flex items-center gap-3 ${active ? runningTone : 'bg-slate-50'}`}>
        <Icon className={`h-5 w-5 shrink-0 ${active ? '' : 'text-slate-500'}`} />
        <div className="min-w-0">
          <div className={`text-sm font-semibold truncate ${active ? '' : 'text-slate-900'}`}>{title}</div>
          <div className={`text-[11px] truncate ${active ? 'opacity-80' : 'text-slate-500'}`}>
            {subtitle}
          </div>
        </div>
      </div>

      <div className={`px-4 py-6 text-center ${active ? runningTone : ''}`}>
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">
          {active ? 'Running' : 'Stopped'}
        </div>
        <div className={`font-display text-4xl sm:text-5xl tabular-nums ${active ? '' : 'text-slate-300'}`}>
          {active ? fmtClock(elapsedSec) : '00:00:00'}
        </div>
        {active && entry.clockIn && (
          <div className="mt-1 text-[11px] opacity-80">
            Started {new Date(entry.clockIn).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-100 space-y-2">
        {!active ? (
          <>
            <Button
              onClick={onStart}
              disabled={busy || startDisabled}
              className={`w-full h-11 text-white ${startTone}`}
              data-testid={`${testId}-start`}
            >
              <Play className="h-4 w-4 mr-2" /> {startLabel}
            </Button>
            {startDisabled && startDisabledHint && (
              <div className="text-[11px] text-center text-slate-500">{startDisabledHint}</div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={onStop}
              disabled={busy}
              className={`col-span-2 h-11 text-white ${stopTone}`}
              data-testid={`${testId}-stop`}
            >
              <Square className="h-4 w-4 mr-2" /> {stopLabel}
            </Button>
            <Button
              variant="outline"
              onClick={onDiscard}
              disabled={busy}
              className="h-11"
              data-testid={`${testId}-discard`}
              title="Discard without saving"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="text-[11px] text-slate-400 flex items-center justify-center gap-1">
          <MapPin className="h-3 w-3" /> GPS recorded on clock-in / out
        </div>
      </div>
    </div>
  );
};

 /** Location auto-match status for the customer clock-in picker. */
const LocationMatchBanner = ({ state, matches, selectedSiteId, onPick }) => {
  if (state === 'locating') {
    return <div className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="location-match-locating"><MapPin className="h-3 w-3" /> Finding sites near you…</div>;
  }
  if (state === 'unavailable' || state === 'none') {
    return (
      <div className="text-[11px] text-slate-500 flex items-center gap-1" data-testid={`location-match-${state}`}>
        <MapPin className="h-3 w-3" />
        {state === 'unavailable'
          ? "Couldn't get your location — pick the work order or site manually. Allow location access so GPS is recorded on clock-in / out."
          : 'No customer site found within range of your location — pick it manually. GPS is recorded on clock-in / out.'}
      </div>
    );
  }
  if (state === 'single') {
    const m = matches[0];
    return (
      <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-1.5" data-testid="location-match-single">
        <MapPin className="h-3.5 w-3.5" /> You're at <strong>{m.site.name}</strong> ({m.site.customerName || m.site.customerId}) · {m.distance} m away
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="location-match-multiple">
      <div className="text-xs text-slate-700 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Several sites are within range — choose yours:</div>
      <div className="flex flex-wrap gap-1.5">
        {matches.map((m) => (
          <button
            key={m.site.id}
            type="button"
            onClick={() => onPick(m)}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${selectedSiteId === m.site.id ? 'border-emerald-600 bg-emerald-50 text-emerald-900' : 'border-slate-200 hover:bg-slate-50 text-slate-700'}`}
            data-testid={`location-match-option-${m.site.id}`}
          >
            {m.site.name} · {m.site.customerName || m.site.customerId} · {m.distance} m
          </button>
        ))}
      </div>
    </div>
  );
};

/** Picker dialog for "Clock in at customer" — lets the engineer pick from
 *  their active work orders first (most common case), or fall back to
 *  the full customer sites list. Also captures an "outside geofence"
 *  reason if the backend rejects the initial attempt with a 422. */
const CustomerClockInDialog = ({ open, onClose, onConfirm, userId, getCoords }) => {
  const [mode, setMode] = useState('wo'); // 'wo' | 'site'
  // Location auto-match: 'locating' | 'single' | 'multiple' | 'none' | 'unavailable'
  const [locState, setLocState] = useState('locating');
  const [matches, setMatches] = useState([]);
  // What the employee changed by hand — auto-match never overrides these.
  const touchedRef = useRef({ mode: false, site: false, wo: false });
  const openSeqRef = useRef(0);
  const [workOrders, setWorkOrders] = useState([]);
  const [sites, setSites] = useState([]);
  const [woId, setWoId] = useState('');
  const [customerFilterId, setCustomerFilterId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [reason, setReason] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Call-out flag — engineer taps this when the visit is an emergency /
  // out-of-hours call-out. Backend consults the matched rate card at
  // billing time and produces a flat-fee line for the first hour plus
  // normal hourly for anything over.
  const [callOut, setCallOut] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset each open so stale selections don't linger.
    setWoId(''); setSiteId(''); setCustomerFilterId(''); setReason(''); setNeedsReason(false);
    setCallOut(false);
    setLocState('locating'); setMatches([]);
    touchedRef.current = { mode: false, site: false, wo: false };
    const seq = ++openSeqRef.current;
    const sitesPromise = getSites().catch(() => []);
    Promise.all([sitesPromise, getCoords ? getCoords() : Promise.resolve(null)]).then(([allSites, coords]) => {
      if (seq !== openSeqRef.current) return;
      if (!coords) { setLocState('unavailable'); return; }
      const found = matchCustomerSites(allSites, coords);
      setMatches(found);
      setLocState(found.length === 0 ? 'none' : found.length === 1 ? 'single' : 'multiple');
      const t = touchedRef.current;
      if (found.length && !t.mode && !t.wo) setMode('site');
      if (found.length === 1 && !t.site) {
        setCustomerFilterId(found[0].site.customerId);
        setSiteId(found[0].site.id);
      }
    });
    Promise.all([getWorkOrders().catch(() => []), sitesPromise])
      .then(([wos, allSites]) => {
        // Engineer's own active WOs — that's the default picker shortlist.
        const mine = wos.filter(w =>
          (w.assigneeId === userId || (w.assigneeIds || []).includes(userId))
          && w.status !== 'completed' && w.status !== 'cancelled'
        );
        setWorkOrders(mine.length ? mine : wos.filter(w => w.status !== 'completed' && w.status !== 'cancelled').slice(0, 50));
        setSites(allSites);
      });
  }, [open, userId]);

  const woById = useMemo(() => Object.fromEntries(workOrders.map(w => [w.id, w])), [workOrders]);

  const confirm = async () => {
    if (mode === 'wo' && !woId) return toast.error('Pick a work order');
    if (mode === 'site' && !siteId) return toast.error('Pick a site');
    setSubmitting(true);
    const res = await onConfirm({
      workOrderId: mode === 'wo' ? woId : '',
      siteId: mode === 'site' ? siteId : '',
      outsideReason: reason,
      callOut,
    });
    setSubmitting(false);
    if (res && res.needsReason) {
      setNeedsReason(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="customer-clockin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Clock in at customer
          </DialogTitle>
          <DialogDescription>
            Pick the work order you're starting — or the site you're arriving at.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'wo' ? 'default' : 'outline'}
            onClick={() => { touchedRef.current.mode = true; setMode('wo'); }}
            className={mode === 'wo' ? 'bg-slate-900' : ''}
            data-testid="mode-wo"
          >
            <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Work order
          </Button>
          <Button
            size="sm"
            variant={mode === 'site' ? 'default' : 'outline'}
            onClick={() => { touchedRef.current.mode = true; setMode('site'); }}
            className={mode === 'site' ? 'bg-slate-900' : ''}
            data-testid="mode-site"
          >
            <MapPin className="h-3.5 w-3.5 mr-1.5" /> Site
          </Button>
        </div>

        <LocationMatchBanner
          state={locState}
          matches={matches}
          selectedSiteId={siteId}
          onPick={(m) => {
            touchedRef.current.site = true;
            setMode('site');
            setCustomerFilterId(m.site.customerId);
            setSiteId(m.site.id);
          }}
        />

        {mode === 'wo' ? (
          <Select value={woId} onValueChange={(v) => { touchedRef.current.wo = true; setWoId(v); }}>
            <SelectTrigger data-testid="picker-wo">
              <SelectValue placeholder="Pick a work order" />
            </SelectTrigger>
            <SelectContent>
              {workOrders.map(w => (
                <SelectItem key={w.id} value={w.id}>
                  <span className="font-mono">{w.id}</span> · {w.title}
                </SelectItem>
              ))}
              {workOrders.length === 0 && (
                <div className="py-2 px-3 text-xs text-slate-500">No open work orders assigned to you.</div>
              )}
            </SelectContent>
          </Select>
        ) : (
          <div className="space-y-2">
            {/* Step 1 — narrow by customer so the site list isn't a
                wall of names. Step 2 — pick the actual site. */}
            <Select value={customerFilterId || 'all'} onValueChange={v => {
              touchedRef.current.site = true;
              setCustomerFilterId(v === 'all' ? '' : v);
              setSiteId('');
            }}>
              <SelectTrigger data-testid="picker-customer">
                <SelectValue placeholder="Filter by customer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">— All customers —</SelectItem>
                {Array.from(
                  sites.reduce((m, s) => {
                    if (s.customerId) m.set(s.customerId, s.customerName || s.customerId);
                    return m;
                  }, new Map())
                ).map(([cid, cname]) => (
                  <SelectItem key={cid} value={cid}>{cname}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={siteId} onValueChange={(v) => { touchedRef.current.site = true; setSiteId(v); }}>
              <SelectTrigger data-testid="picker-site">
                <SelectValue placeholder="Pick a site" />
              </SelectTrigger>
              <SelectContent>
                {(customerFilterId
                  ? sites.filter(s => s.customerId === customerFilterId)
                  : sites
                ).map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {needsReason && (
          <div className="space-y-2">
            <div className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> You're outside the site geofence — add a reason to continue.
            </div>
            <input
              type="text"
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
              placeholder="e.g. Customer redirected me to loading bay"
              value={reason}
              onChange={e => setReason(e.target.value)}
              data-testid="outside-reason-input"
            />
          </div>
        )}

        {woId && woById[woId] && (
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            <Truck className="h-3 w-3 inline mr-1" />
            Heading to {woById[woId].title}
          </div>
        )}

        {/* Call-out toggle — emergency / out-of-hours visits. Backend
            resolves the flat fee from the matched rate card at
            billing time; if that rule has no call-out fee configured,
            the flag falls back to normal hourly billing. */}
        <label
          className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${callOut ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
          data-testid="callout-toggle"
        >
          <input
            type="checkbox"
            checked={callOut}
            onChange={(e) => setCallOut(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
              <AlertTriangle className={`h-3.5 w-3.5 ${callOut ? 'text-amber-600' : 'text-slate-400'}`} />
              This is a call-out
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Bills as a flat fee for the first hour on site (per the customer&rsquo;s rate card), then normal hourly for anything over. Ask the office if you&rsquo;re not sure.
            </div>
          </div>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={confirm}
            disabled={submitting || (needsReason && !reason.trim())}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="confirm-customer-clockin"
          >
            <Play className="h-4 w-4 mr-2" />
            {submitting ? 'Clocking in…' : 'Clock in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/**
 *  Reason prompt for clocking in at Lutrom HQ while outside the
 *  geofence. Pattern mirrors the customer-lane dialog (soft warn + free
 *  text reason + confirm).
 */
const HqReasonDialog = ({ open, info, reason, onReasonChange, onCancel, onConfirm, busy }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <DialogContent className="sm:max-w-md" data-testid="hq-reason-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Home className="h-5 w-5 text-blue-700" /> Clock in at Lutrom
        </DialogTitle>
        <DialogDescription>
          You&rsquo;re outside the {info?.siteName || 'Lutrom HQ'} geofence.
          Add a reason to continue &mdash; it&rsquo;s recorded on the entry for the admin.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div
          className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900"
          data-testid="hq-distance-banner"
        >
          <div className="flex items-center gap-1.5 font-medium mb-0.5">
            <AlertTriangle className="h-4 w-4" />
            You&rsquo;re <span className="tabular-nums">{info?.distance ?? '?'}m</span> from HQ
          </div>
          <div className="text-xs text-amber-800">
            Geofence radius: <span className="tabular-nums">{info?.radius ?? 250}m</span>. Type a
            short reason so the admin knows why.
          </div>
        </div>

        <input
          type="text"
          className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
          placeholder="e.g. Van already loaded at depot, heading straight to site"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          data-testid="hq-outside-reason-input"
          autoFocus
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={busy || !reason.trim()}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="confirm-hq-clockin"
        >
          <Play className="h-4 w-4 mr-2" />
          {busy ? 'Clocking in…' : 'Clock in anyway'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const TodaySummary = ({ today, anyActive }) => {
  const dayTotal = today.clockedHours || 0;
  const custTotal = today.customerHours || 0;
  const custWo = today.customerWoHours || 0;
  const custUnacc = today.customerUnaccounted ?? Math.max(0, custTotal - custWo);
  const hqTotal = today.atLutromHours ?? Math.max(0, dayTotal - custTotal);
  const hqWo = today.lutromWoHours || 0;
  const hqUnacc = today.lutromUnaccounted ?? Math.max(0, hqTotal - hqWo);

  return (
    <div className="space-y-2" data-testid="today-summary-card">
      {/* Hero — the whole working day at a glance. Tone flips green
          while the engineer is on the clock so it doubles as a "you're
          being timed" indicator. */}
      <div
        className={`rounded-md border p-3 sm:p-4 flex items-center justify-between gap-3 ${
          anyActive
            ? 'bg-emerald-700 border-emerald-700 text-white'
            : 'bg-slate-900 border-slate-900 text-white'
        }`}
        data-testid="today-summary-hero"
      >
        <div className="flex items-center gap-2 opacity-90 text-[10px] uppercase tracking-wider">
          <Clock className="h-3.5 w-3.5" />
          <span>Total for the day</span>
        </div>
        <div className="font-display text-2xl sm:text-3xl tabular-nums leading-tight">
          {fmtHours(dayTotal)}
        </div>
      </div>

      {/* Two-column split — everything the engineer needs to spot a
          leak (unaccounted time) without scrolling. Each column packs
          Total → On WO → Unaccounted into 3 tight rows. */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <BreakdownCard
          icon={Building2}
          label="At customer"
          tone="bg-emerald-50 border-emerald-200 text-emerald-900"
          total={custTotal}
          woHours={custWo}
          unaccounted={custUnacc}
          testId="today-breakdown-customer"
        />
        <BreakdownCard
          icon={Home}
          label="At Lutrom (HQ + travel)"
          tone="bg-sky-50 border-sky-200 text-sky-900"
          total={hqTotal}
          woHours={hqWo}
          unaccounted={hqUnacc}
          testId="today-breakdown-lutrom"
        />
      </div>
    </div>
  );
};

/**
 * Compact 3-row breakdown card — Total on top, "On WO" row + Unaccounted
 * row underneath. Unaccounted row switches to an amber tone when > 30min
 * so the engineer's eye is drawn to it. Tabular numbers keep the two
 * columns aligned across the grid.
 */
const BreakdownCard = ({ icon: Icon, label, tone, total, woHours, unaccounted, testId }) => {
  const leaking = unaccounted > 0.5;
  return (
    <div className={`rounded-md border p-2.5 sm:p-3 ${tone}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 opacity-80 text-[10px] uppercase tracking-wider">
        <Icon className="h-3 w-3" />
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-0.5 font-display text-xl sm:text-2xl tabular-nums leading-tight">
        {fmtHours(total)}
      </div>
      <div className="mt-2 space-y-1 text-[11px]">
        <div className="flex items-center justify-between gap-2">
          <span className="opacity-75 inline-flex items-center gap-1">
            <Briefcase className="h-3 w-3" /> On WO
          </span>
          <span className="tabular-nums font-medium" data-testid={`${testId}-wo`}>
            {fmtHours(woHours)}
          </span>
        </div>
        <div className={`flex items-center justify-between gap-2 ${leaking ? 'text-amber-800' : 'opacity-75'}`}>
          <span className="inline-flex items-center gap-1">
            <AlertTriangle className={`h-3 w-3 ${leaking ? '' : 'opacity-60'}`} /> Unaccounted
          </span>
          <span
            className={`tabular-nums ${leaking ? 'font-semibold' : ''}`}
            data-testid={`${testId}-unaccounted`}
          >
            {fmtHours(unaccounted)}
          </span>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className={`rounded-md border p-3 sm:p-4 ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 opacity-80 text-[10px] uppercase tracking-wider">
      <Icon className="h-3 w-3" />
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-1 font-display text-2xl sm:text-3xl tabular-nums leading-tight">{value}</div>
  </div>
);

const fmtHours = (n) => {
  const total = Math.max(0, Number(n) || 0);
  const h = Math.floor(total);
  const m = Math.round((total - h) * 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

// Auto-opens when an engineer has an open clock-in older than the
// threshold (typically because they forgot to tap Stop the previous
// day). Single text+time form so it submits in two taps. Backend flags
// `forgotToClockOut=True` and pushes a notification to the admin role
// so the claimed time can be sanity-checked from Timesheets.
const ForgotClockOutDialog = ({
  target, time, note, saving,
  onTimeChange, onNoteChange, onCancel, onConfirm,
}) => {
  if (!target) return null;
  const startedDate = target.__startedDate || new Date(target.clockIn);
  const dateLabel = startedDate.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  const startedTime = startedDate.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit',
  });
  const lane = target.kind === 'lutrom' ? 'Lutrom HQ' : (target.siteName || 'a customer site');
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="forgot-clockout-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Looks like you forgot to clock out
          </DialogTitle>
          <DialogDescription>
            You clocked in at <strong>{lane}</strong> on <strong>{dateLabel}</strong> at <strong>{startedTime}</strong> and never tapped Stop.
            What time did you actually finish? An admin will get a notification to double-check the time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">
              Finish time
            </label>
            <Input
              type="time"
              value={time}
              onChange={(e) => onTimeChange(e.target.value)}
              className="h-11 text-lg"
              data-testid="forgot-clockout-time"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">
              Optional note for the admin
            </label>
            <Input
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="e.g. Got home, tablet was on charge"
              data-testid="forgot-clockout-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="forgot-clockout-later">
            Remind me later
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving || !time}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="forgot-clockout-confirm"
          >
            {saving ? 'Saving…' : 'Save & notify admin'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TimeClock;
